import React from 'react';
import ReactPixel from 'react-facebook-pixel';
import moment from 'moment';
import browserStorage from './browserStorage';

export const AnalyticsPlatforms = {
	FacebookPixel: 'FB',
	GoogleAnalytics: 'GA'
};

let enableFacebookPixel = false;
let enableGATracking = false;
let isUATrackingId = false;

const gtag = (...args) => {
	if (typeof window !== 'undefined') {
		if (typeof window.gtag === 'undefined') {
			window.dataLayer = window.dataLayer || [];
			window.gtag = function gtag() {
				window.dataLayer.push(arguments);
			};
		}

		window.gtag(...args);
	}
};

const loadGTag = (googleAnalyticsCode) => {
	const gtagJs = document.createElement('script');
	gtagJs.src = `https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsCode}`;
	gtagJs.async = true;
	gtagJs.onload = () => {
		gtag('js', new Date());
		gtag('config', googleAnalyticsCode, {
			'linker': {
				'accept_incoming': true
			}
		});
	};
	document.body && document.body.appendChild(gtagJs);
};

const initAnalyticsTracking = (franchisor, { facebookPixelId, googleAnalyticsCode }) => {
	if (facebookPixelId) {
		const options = {
			autoConfig: false,
			debug: false,
		};
		ReactPixel.init(facebookPixelId, {}, options);
		enableFacebookPixel = true;
	} else {
		enableFacebookPixel = false;
	}

	if (googleAnalyticsCode) {
		loadGTag(googleAnalyticsCode);
		enableGATracking = true;
		isUATrackingId = googleAnalyticsCode.startsWith('UA-');	// Universal Analytics tracking id

		initCampaignTracking(getCampaignTags(franchisor));
	}
	else {
		enableGATracking = false;
	}
};

const getCampaignTags = (franchisor) => {
	let campaignTags = null;
	const storedCampaign = browserStorage.getFromLocalStorage(franchisor, 'campaign');
	if (storedCampaign) {
		// use from local storage within 7 days of saving
		const campaign = JSON.parse(storedCampaign);
		if (moment(campaign.timestamp).isAfter(moment().subtract(7, 'days'))) {
			campaignTags = campaign.tags;
		}
	}

	return campaignTags;
};

const setCampaignTags = (franchisor, campaignTags) => {
	if (campaignTags) {
		// store in local storage with current timestamp
		browserStorage.setToLocalStorage(franchisor, 'campaign', JSON.stringify({ tags: campaignTags, timestamp: moment() }));

		initCampaignTracking(campaignTags);
	}
};

const initCampaignTracking = (campaignTags) => {
	if (campaignTags && enableGATracking) {
		// doesn't work with UA
		//gtag('set', {
		//	'campaign_name': campaignTags.utm_campaign,
		//	'campaign_source': campaignTags.utm_source,
		//	'campaign_medium': campaignTags.utm_medium
		//});

		gtag('set', {
			campaign: {
				name: campaignTags.utm_campaign,
				source: campaignTags.utm_source,
				medium: campaignTags.utm_medium
			}
		});
	}
};

const trackEvent = (eventCode, eventData, eventPlatform, isCustomEvent = false) => {
	const platforms = eventPlatform ? (Array.isArray(eventPlatform) ? eventPlatform : [eventPlatform]) : Object.values(AnalyticsPlatforms);

	if (enableFacebookPixel && platforms.includes(AnalyticsPlatforms.FacebookPixel)) {
		if (isCustomEvent) {
			ReactPixel.trackCustom(eventCode, eventData);
		} else {
			ReactPixel.track(eventCode, eventData);	// FB Pixel js has validation for standard events parameters
		}
	}

	if (enableGATracking && platforms.includes(AnalyticsPlatforms.GoogleAnalytics)) {
		gtag('event', eventCode, eventData);
	}
};

const trackPageView = (page) => {
	if (enableFacebookPixel) {
		ReactPixel.pageView();
	}

	// Manually send page views for Universal Analytics tracking IDs only.
	// GA4 Measurement IDs send page views automatically as long as "Enhanced measurement" => "Page views" => "Page changes based on browser history events" is enabled
	if (enableGATracking && isUATrackingId) {
		gtag('event', 'page_view', { page_path: page });
	}
};

export default {
	initAnalyticsTracking,
	getCampaignTags,
	setCampaignTags,
	trackEvent,
	trackPageView
};

export const withAnalytics = Component =>
	class WithAnalytics extends React.Component {
		componentDidMount() {
			const page = this.props.location.pathname;
			this.trackPage(page);
		}

		componentWillReceiveProps(nextProps) {
			const currentPage = this.props.location.pathname;
			const nextPage = nextProps.location.pathname;

			// trigger analytics tracking after franchisor details (FacebookPixelId, etc.) are loaded
			const currentFranchisor = this.props.franchisor;
			const nextFranchisor = nextProps.franchisor;

			if (currentPage !== nextPage || currentFranchisor.id !== nextFranchisor.id) {
				this.trackPage(nextPage);
			}
		}

		trackPage(page) {
			trackPageView(page);
		}

		render() {
			return <Component {...this.props} />;
		}
	};